import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus, Trash2, Upload } from 'lucide-react'
import { useState } from 'react'
import { Controller, useFieldArray, useForm, useWatch, type UseFormReturn } from 'react-hook-form'

import { Alert } from '@/components/feedback/Alert'
import { ErrorState } from '@/components/feedback/ErrorState'
import { FieldError } from '@/components/feedback/FieldError'
import { Loading } from '@/components/feedback/Loading'
import { useToastStack } from '@/components/feedback/toast-stack'
import { OptionSelect } from '@/components/filters/OptionSelect'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { useCashFirm, useInvalidateCash } from '@/features/cash/hooks'
import {
  settingsBody,
  settingsFormSchema,
  settingsFormValues,
  settingsServerField,
  type SettingsFormValues,
} from '@/features/cash/settings-form'
import { CashFirmSelect } from '@/features/cash/shared'
import { HrPageHeader as PageHeader } from '@/features/hr/HrPageHeader'
import { formatDateTime } from '@/lib/format'
import { fieldErrorProps } from '@/lib/form/field-error'
import { GuardScope, useGuardRegistry, useRegisterGuard } from '@/lib/form/guard-registry'
import { useRecordEditor } from '@/lib/form/use-record-editor'
import { useRecordVersion } from '@/lib/form/use-record-version'
import { messages } from '@/lib/messages'
import { toProblem } from '@/lib/problem'
import { queryKeys } from '@/lib/query-keys'
import { cashApi, type CashSettings } from '@/services/http/cash'

const t = messages.cash.settings
const CLASSES = ['Ödeme', 'Avans', 'Kontrol', 'Hariç']

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="bg-surface flex flex-col gap-3 rounded-lg border p-4" aria-label={title}>
      <h2 className="text-sm font-semibold">{title}</h2>
      {children}
    </section>
  )
}

/** Sınıf ve grup carisi çiftleri: formda `{key, value}` listesi (sunucuda sözlük). */
function PairsEditor({
  form,
  name,
  keyLabel,
  valueLabel,
  addLabel,
  valueOptions,
}: {
  form: UseFormReturn<SettingsFormValues>
  name: 'classes' | 'group_caris'
  keyLabel: string
  valueLabel: string
  addLabel: string
  valueOptions?: string[]
}) {
  const pairs = useFieldArray({ control: form.control, name })
  const current = useWatch({ control: form.control, name })
  const rootError = form.formState.errors[name]
  const message = rootError?.message ?? rootError?.root?.message
  const id = `cash-settings-${name}`
  return (
    <div className="flex flex-col gap-2">
      {pairs.fields.map((field, i) => {
        // Sunucunun sözlük yollu 422'si (`classes.<kod>`) `settingsServerField` ile bu satıra düşer.
        const rowError = form.formState.errors[name]?.[i]
        const keyMessage = rowError?.key?.message
        const valueMessage = rowError?.value?.message
        const rowId = `${id}-${i}`
        return (
          <div key={field.id} className="flex flex-col gap-1">
            <div className="flex flex-wrap items-center gap-2">
              <Input
                className="w-40"
                aria-label={`${keyLabel} ${i + 1}`}
                {...fieldErrorProps(`${rowId}-key`, keyMessage)}
                {...form.register(`${name}.${i}.key`)}
              />
              {valueOptions ? (
                <Controller
                  control={form.control}
                  name={`${name}.${i}.value`}
                  render={({ field: value }) => (
                    <OptionSelect
                      value={value.value}
                      options={valueOptions.map((o) => ({ value: o, label: o }))}
                      onValueChange={value.onChange}
                      aria-label={`${valueLabel} ${i + 1}`}
                      className="w-40"
                    />
                  )}
                />
              ) : (
                <Input
                  className="w-56"
                  aria-label={`${valueLabel} ${i + 1}`}
                  {...fieldErrorProps(`${rowId}-value`, valueMessage)}
                  {...form.register(`${name}.${i}.value`)}
                />
              )}
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                aria-label={`${t.remove} ${current[i]?.key ?? ''}`}
                onClick={() => pairs.remove(i)}
              >
                <Trash2 />
              </Button>
            </div>
            <FieldError id={`${rowId}-key`} message={keyMessage} />
            <FieldError id={`${rowId}-value`} message={valueMessage} />
          </div>
        )
      })}
      <FieldError id={id} message={message} />
      <Button
        type="button"
        variant="secondary"
        size="sm"
        className="w-fit"
        onClick={() => pairs.append({ key: '', value: valueOptions?.[0] ?? '' })}
      >
        <Plus />
        {addLabel}
      </Button>
    </div>
  )
}

function SettingsForm({
  firm,
  settings,
  onReloaded,
}: {
  firm: string
  settings: CashSettings
  /** "Yeniden yükle" sonrası: sayfa formu güncel ayarlardan baştan kurar. */
  onReloaded: () => void
}) {
  const form = useForm<SettingsFormValues>({
    resolver: zodResolver(settingsFormSchema),
    defaultValues: settingsFormValues(settings),
  })
  const types = useFieldArray({ control: form.control, name: 'types' })
  const errors = form.formState.errors
  const { toast } = useToastStack()
  const invalidate = useInvalidateCash()
  const queryClient = useQueryClient()
  // Taslak açılışta ayarlardan kopyalanır: sürüm o anki sürüme sabit kalır (`dirty: true`), arka plan
  // tazelemesi ya da gerçek zamanlı invalidation onu ilerletmez; yeni sürüm yalnız kendi kaydımızdan gelir.
  const version = useRecordVersion({
    recordId: firm,
    loaded: settings.version,
    dirty: true,
    reload: async () => {
      await queryClient.fetchQuery({
        queryKey: queryKeys.cash.settings(firm),
        queryFn: () => cashApi.settings(firm),
        staleTime: 0,
      })
      onReloaded()
    },
  })
  const editor = useRecordEditor({
    form,
    submit: (values) => cashApi.saveSettings(settingsBody(firm, values, version.current())),
    onSaved: (saved) => {
      version.advance(saved.version)
      // Sunucunun normalleştirdiği hâl (boş satırlar atılmış) formun yerine geçer.
      form.reset(settingsFormValues(saved))
      toast({ type: 'success', title: t.saved })
      void invalidate()
    },
    version,
    serverFields: settingsServerField,
    failedTitle: messages.cash.planning.failed,
  })
  useRegisterGuard(editor.requestClose)

  return (
    <form className="flex flex-col gap-4" noValidate onSubmit={(event) => void editor.save(event)}>
      {version.alert}
      {version.dialog}
      {editor.dialog}
      {settings.is_default ? (
        <Alert tone="info" title={t.defaults} />
      ) : (
        settings.updated_at && (
          <p className="text-text-secondary text-xs">
            {t.lastChange(
              settings.updated_by_name ?? messages.common.unknown,
              formatDateTime(settings.updated_at),
            )}
          </p>
        )
      )}
      <Section title={t.title}>
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1 text-xs">
            {t.minCash}
            <Input
              inputMode="decimal"
              className="w-44"
              aria-label={t.minCash}
              {...fieldErrorProps('cash-settings-min-cash', errors.min_cash?.message)}
              {...form.register('min_cash')}
            />
            <FieldError id="cash-settings-min-cash" message={errors.min_cash?.message} />
          </label>
          <label className="flex flex-col gap-1 text-xs">
            {t.weekday}
            <Controller
              control={form.control}
              name="payment_weekday"
              render={({ field }) => (
                <OptionSelect
                  value={field.value}
                  options={t.weekdays.map((label, i) => ({ value: String(i + 1), label }))}
                  onValueChange={field.onChange}
                  aria-label={t.weekday}
                  className="w-40"
                />
              )}
            />
            <FieldError id="cash-settings-weekday" message={errors.payment_weekday?.message} />
          </label>
          <label className="flex flex-col gap-1 text-xs">
            {t.deadlineDay}
            <Controller
              control={form.control}
              name="entry_deadline_weekday"
              render={({ field }) => (
                <OptionSelect
                  value={field.value}
                  options={[
                    { value: '', label: t.noDeadline },
                    ...t.allWeekdays.map((label, i) => ({ value: String(i + 1), label })),
                  ]}
                  onValueChange={field.onChange}
                  aria-label={t.deadlineDay}
                  className="w-40"
                />
              )}
            />
          </label>
          <label className="flex flex-col gap-1 text-xs">
            {t.deadlineTime}
            <Input
              type="time"
              className="w-32"
              aria-label={t.deadlineTime}
              {...fieldErrorProps(
                'cash-settings-deadline-time',
                errors.entry_deadline_time?.message,
              )}
              {...form.register('entry_deadline_time')}
            />
            <FieldError
              id="cash-settings-deadline-time"
              message={errors.entry_deadline_time?.message}
            />
          </label>
          <label className="flex flex-col gap-1 text-xs">
            {t.pessimisticCollection}
            <Input
              inputMode="numeric"
              className="w-24"
              aria-label={t.pessimisticCollection}
              {...fieldErrorProps(
                'cash-settings-pessimistic-collection',
                errors.pessimistic_collection_days?.message,
              )}
              {...form.register('pessimistic_collection_days')}
            />
            <FieldError
              id="cash-settings-pessimistic-collection"
              message={errors.pessimistic_collection_days?.message}
            />
          </label>
          <label className="flex flex-col gap-1 text-xs">
            {t.pessimisticCheque}
            <Input
              inputMode="numeric"
              className="w-24"
              aria-label={t.pessimisticCheque}
              {...fieldErrorProps(
                'cash-settings-pessimistic-cheque',
                errors.pessimistic_cheque_days?.message,
              )}
              {...form.register('pessimistic_cheque_days')}
            />
            <FieldError
              id="cash-settings-pessimistic-cheque"
              message={errors.pessimistic_cheque_days?.message}
            />
          </label>
          <label className="flex flex-col gap-1 text-xs">
            {t.rule}
            <Controller
              control={form.control}
              name="payment_rule"
              render={({ field }) => (
                <OptionSelect
                  value={field.value}
                  options={Object.entries(t.rules).map(([value, label]) => ({ value, label }))}
                  onValueChange={field.onChange}
                  aria-label={t.rule}
                  className="w-72"
                />
              )}
            />
          </label>
        </div>
      </Section>
      <Section title={t.types}>
        <div className="overflow-x-auto">
          <table className="text-sm">
            <thead>
              <tr className="text-text-secondary text-left text-xs">
                <th className="py-1 pr-3 font-medium">{t.typeName}</th>
                <th className="py-1 pr-3 font-medium">{t.direction}</th>
                <th className="py-1 pr-3 font-medium">{t.day}</th>
                <th className="py-1 pr-3 font-medium">{t.monthOffset}</th>
              </tr>
            </thead>
            <tbody>
              {types.fields.map((x, i) => {
                const dayError = errors.types?.[i]?.day?.message
                const offsetError = errors.types?.[i]?.month_offset?.message
                return (
                  <tr key={x.id} className="align-top">
                    <td className="py-1 pr-3">{x.name}</td>
                    <td className="text-text-secondary py-1 pr-3">{x.direction}</td>
                    <td className="py-1 pr-3">
                      <Input
                        inputMode="numeric"
                        className="w-20"
                        aria-label={`${x.name} ${t.day}`}
                        {...fieldErrorProps(`cash-settings-type-${i}-day`, dayError)}
                        {...form.register(`types.${i}.day`)}
                      />
                      <FieldError id={`cash-settings-type-${i}-day`} message={dayError} />
                    </td>
                    <td className="py-1 pr-3">
                      <Input
                        inputMode="numeric"
                        className="w-20"
                        aria-label={`${x.name} ${t.monthOffset}`}
                        {...fieldErrorProps(`cash-settings-type-${i}-offset`, offsetError)}
                        {...form.register(`types.${i}.month_offset`)}
                      />
                      <FieldError id={`cash-settings-type-${i}-offset`} message={offsetError} />
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        <FieldError
          id="cash-settings-types"
          message={errors.types?.message ?? errors.types?.root?.message}
        />
      </Section>
      <Section title={t.classes}>
        <PairsEditor
          form={form}
          name="classes"
          keyLabel={t.classCode}
          valueLabel={t.klass}
          addLabel={t.addClass}
          valueOptions={CLASSES}
        />
      </Section>
      <Section title={t.groupCaris}>
        <PairsEditor
          form={form}
          name="group_caris"
          keyLabel={t.groupCode}
          valueLabel={t.groupName}
          addLabel={t.addGroup}
        />
      </Section>
      <Button type="submit" className="w-fit" loading={editor.saving}>
        {t.save}
      </Button>
    </form>
  )
}

function ImportSection({ firm }: { firm: string }) {
  const [file, setFile] = useState<File | null>(null)
  const [replace, setReplace] = useState(false)
  const { toast } = useToastStack()
  const invalidate = useInvalidateCash()
  const run = useMutation({
    mutationFn: () => cashApi.importExcel(firm, file!, replace),
    onSuccess: (result) => {
      toast({ type: 'success', title: t.imported(result) })
      void invalidate()
    },
    onError: (error) =>
      toast({
        type: 'error',
        title: messages.cash.planning.failed,
        description: toProblem(error).detail ?? undefined,
      }),
  })
  return (
    <Section title={t.importTitle}>
      <p className="text-text-secondary text-sm">{t.importDescription}</p>
      <div className="flex flex-wrap items-center gap-3">
        <Input
          type="file"
          accept=".xlsx"
          className="w-80"
          aria-label={t.importFile}
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
        />
        <label className="flex items-center gap-2 text-sm">
          <Checkbox checked={replace} onCheckedChange={(v) => setReplace(!!v)} />
          {t.importReplace}
        </label>
        <Button disabled={!file} loading={run.isPending} onClick={() => run.mutate()}>
          <Upload />
          {t.importRun}
        </Button>
      </div>
    </Section>
  )
}

/** /nakit/ayarlar — firma bazında nakit planı ayarları (docs/NAKIT-PLANI-PLAN.md Faz 2). */
export function SettingsPage() {
  const { firms, firm, setFirm, can } = useCashFirm()
  const canEdit = can('cash.settings')
  const [epoch, setEpoch] = useState(0)
  // Firma değişimi formu baştan kurar; kirliyse önce "Kaydedilmemiş değişiklikler" sorulur.
  const guards = useGuardRegistry()
  const settings = useQuery({
    queryKey: queryKeys.cash.settings(firm ?? ''),
    queryFn: () => cashApi.settings(firm!),
    enabled: !!firm,
  })
  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title={t.title}
        description={t.description}
        actions={
          <CashFirmSelect
            firms={firms.data ?? []}
            firm={firm}
            onChange={(next) => guards.guarded(() => setFirm(next))}
          />
        }
      />
      {settings.isError ? (
        <ErrorState error={settings.error} onRetry={() => void settings.refetch()} />
      ) : !settings.data || !firm ? (
        <Loading rows={6} />
      ) : (
        <fieldset disabled={!canEdit} className="flex flex-col gap-4">
          {/* Taslak yalnız firma değişince ya da "Yeniden yükle"de baştan kurulur; arka plan tazelemesi
              kullanıcının yerel değişikliğini silmez (sürüm kapısı eski sürümü 409'la yakalar). */}
          <GuardScope value={guards}>
            <SettingsForm
              key={`${firm}-${epoch}`}
              firm={firm}
              settings={settings.data}
              onReloaded={() => setEpoch((e) => e + 1)}
            />
          </GuardScope>
          <ImportSection firm={firm} />
        </fieldset>
      )}
    </div>
  )
}
